import React, { useEffect, useState, useRef } from 'react'
import {
  Alert,
  Button,
  Col,
  Dropdown,
  Form,
  Icon,
  Menu,
  notification,
  Row
} from 'antd'
import { ClickParam } from 'antd/lib/menu'
import { ColumnProps } from 'antd/lib/table'
import { Formik } from 'formik'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ApplicationState } from 'src/store'
import { ClientNotifyCatch } from 'src/config/bugsnag'
import { Drawer, PageHeader } from 'src/components/Ant'
import {
  getAgreements,
  setAgreements
} from 'src/services/api/agencies-agreements'
import { Input, InputCurrency } from 'src/components/Formik'
import { Organization, AgreementServiceHub, Agreement } from 'src/types'
import history from 'src/config/routes/history'
import Select, { Option } from 'src/components/Formik/Select'
import SelectServiceHub from 'src/components/Formik/select-service-hub'
import useDocumentTitle from 'src/hooks/use-document-title'

import { defaultValues, initialValues } from './initial-values'
import * as CSS from './styles'
import validationSchema from './validation-schema'

const agreementTypeOptions: Option[] = [
  {
    label: 'AIR',
    value: 'AIR',
    title: 'AIR'
  },
  {
    label: 'HOTEL',
    value: 'HOTEL',
    title: 'HOTEL'
  },
  {
    label: 'VEHICLE',
    value: 'VEHICLE',
    title: 'VEHICLE'
  }
]
const integrationTypeOptions: Option[] = [
  {
    label: 'CONSOLIDATOR',
    value: 'CONSOLIDATOR',
    title: 'CONSOLIDATOR'
  },
  {
    label: 'PARTNER',
    value: 'PARTNER',
    title: 'PARTNER'
  },
  {
    label: 'GDS',
    value: 'GDS',
    title: 'GDS'
  }
]

enum LoadInfo {
  isIdle,
  isLoading,
  isLoaded,
  hasError
}

export default function Agreements() {
  useDocumentTitle('web.agreement.title')

  const { me } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  const [list, setList] = useState<any[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [loading, setLoading] = useState<LoadInfo>(LoadInfo.isIdle)

  const refFormik = useRef(null)
  const { t } = useTranslation()

  useEffect(() => {
    setLoading(LoadInfo.isLoaded)
    getAgreements(currentPage, me.user.organization_id)
      .then(function (response) {
        if (response.status === 200) {
          setList(response.data.agreements)
          setLoading(LoadInfo.isLoaded)
          return
        }
        setLoading(LoadInfo.hasError)
      })
      .catch(function () {
        setLoading(LoadInfo.hasError)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  const columns: ColumnProps<any>[] = [
    {
      align: 'center',
      dataIndex: 'code',
      key: 'code',
      render: (value: string) => value,
      title: 'web.resources.common.code.title'
    },
    {
      align: 'center',
      dataIndex: 'agreement_type',
      key: 'agreement_type',
      render: (value: string) => value,
      title: 'web.agreement.agreement-type'
    },
    {
      align: 'center',
      dataIndex: 'integration_type',
      key: 'integration_type',
      render: (value: string) => value,
      title: 'web.agreement.integration-type'
    },
    {
      align: 'center',
      dataIndex: 'name',
      key: 'name',
      render: (value: string) => value,
      title: 'web.agreement.name'
    },
    {
      align: 'center',
      dataIndex: 'organization',
      key: 'organization',
      render: (value: Organization) => <div>{value?.trade_name}</div>,
      title: 'web.resources.organizations.title'
    },
    {
      align: 'center',
      dataIndex: 'service_hub',
      key: 'service_hub',
      render: (value: AgreementServiceHub) => <div>{value?.name}</div>,
      title: 'web.agreement.service-hub'
    },
    {
      dataIndex: '',
      key: 'actions',
      render: (record: Agreement) => (
        <Dropdown
          overlay={
            <Menu
              onClick={({ key }: ClickParam) => {
                if (key === 'set-active') {
                  setAgreements(
                    { ...record, active: false },
                    record.organization_id
                  )
                    .then(function (response) {
                      if (response.status === 200) {
                        notification.success({
                          message: t('common.edit-done-successfully'),
                          placement: 'bottomRight'
                        })
                        return
                      }
                      notification.error({
                        message: t('common.response.status-500'),
                        placement: 'bottomRight'
                      })
                      ClientNotifyCatch(
                        'Could not setAgreements /agencies/agreements',
                        record
                      )
                    })
                    .catch(function () {
                      notification.error({
                        message: t('common.response.status-500'),
                        placement: 'bottomRight'
                      })
                      ClientNotifyCatch(
                        'Could not setAgreements /agencies/agreements',
                        record
                      )
                    })
                  return
                }

                refFormik.current?.resetForm(record)
              }}
            >
              <Menu.Item key="edit">{t('common.actions.edit')}</Menu.Item>
              <Menu.Item key="set-active">
                {t('common.actions.inactivate')}
              </Menu.Item>
            </Menu>
          }
          placement="bottomRight"
        >
          <Button type="link">
            <Icon type="more" />
          </Button>
        </Dropdown>
      ),
      title: ''
    }
  ]

  function handleSubmit(values: typeof initialValues): void {
    const sanitized = JSON.parse(JSON.stringify(values))
    delete sanitized.key
    delete sanitized.organization
    delete sanitized.service_hub

    if (typeof sanitized.agreement_id === 'string') {
      sanitized.agreement_id = null
    }

    setAgreements(sanitized, me.user.organization_id)
      .then(function (response) {
        if (response.status === 200) {
          setList(response.data.agreements)
          return
        }
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
        ClientNotifyCatch(
          'Could not setAgreements /agencies/agreements',
          response
        )
      })
      .catch(function (response) {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
        ClientNotifyCatch(
          'Could not setAgreements /agencies/agreements',
          response
        )
      })
  }

  if (loading === LoadInfo.hasError) {
    return (
      <>
        <PageHeader
          onBack={() => {
            history.goBack()
          }}
          title="web.agencies.issuances.title"
        />
        <Alert
          message={t('common.response.status-500')}
          showIcon
          type="error"
        />
      </>
    )
  }

  return (
    <>
      <PageHeader
        onBack={() => {
          history.goBack()
        }}
        title="web.agreement.title"
        extra={[
          <Button
            key="add-new"
            onClick={() => {
              refFormik.current?.resetForm({
                ...defaultValues,
                organization_id: me.user.organization_id
              })
            }}
          >
            {t('common.actions.add')}
          </Button>
        ]}
      />
      <CSS.Table
        className="table-striped"
        columns={columns}
        currentPage={currentPage}
        pagination={{
          pageSize: 10
        }}
        dataSource={list}
        loading={loading === LoadInfo.isLoading}
        onChange={page => {
          setCurrentPage(page.current)
        }}
      />
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        ref={refFormik}
        render={formikProps => {
          const isPartner = formikProps.values.integration_type === 'PARTNER'
          const isServiceAir = formikProps.values.agreement_type === 'AIR'

          return (
            <Drawer
              footer={
                <CSS.Div>
                  <Button
                    onClick={async () => {
                      formikProps.resetForm(initialValues)
                    }}
                    type="default"
                  >
                    {t('common.actions.cancel')}
                  </Button>
                  <Button
                    onClick={async () => {
                      await formikProps.submitForm()
                      const validateForm = await formikProps.validateForm()
                      if (Object.keys(validateForm).length === 0) {
                        formikProps.resetForm(initialValues)
                      }
                    }}
                    type="primary"
                  >
                    {t('common.actions.save')}
                  </Button>
                </CSS.Div>
                // end footer **************************
              }
              onClose={() => {
                formikProps.resetForm(initialValues)
              }}
              title={t(
                typeof formikProps.values.agreement_id === 'string'
                  ? 'common.actions.add'
                  : 'common.actions.edit'
              )}
              visible={Boolean(formikProps.values.agreement_id)}
              width="45%"
            >
              <Form style={{ marginBottom: '3em' }}>
                <Row gutter={[12, 0]}>
                  <Col span={12}>
                    <Select
                      dataTestId="agreement-type"
                      defaultActiveFirstOption
                      formikProps={formikProps}
                      label="web.agreement.agreement-type"
                      name="agreement_type"
                      onChange={function (value) {
                        if (value === 'AIR') {
                          formikProps.setFieldValue('access_code', '')
                          formikProps.setFieldValue('domain', '')
                          return
                        }
                        formikProps.setFieldValue('gtw_agency_id', '')
                        formikProps.setFieldValue('gtw_group_id', '')
                        formikProps.setFieldValue('package_group', '')
                      }}
                      options={agreementTypeOptions}
                    />
                  </Col>
                  <Col span={12}>
                    <Select
                      dataTestId="integration-type"
                      formikProps={formikProps}
                      label="web.agreement.integration-type"
                      name="integration_type"
                      onChange={function (value) {
                        if (value === 'PARTNER') {
                          formikProps.setFieldValue('access_code', '')
                          formikProps.setFieldValue('branch_id', '')
                          formikProps.setFieldValue('domain', '')
                          formikProps.setFieldValue('gtw_agency_id', '')
                          formikProps.setFieldValue('gtw_group_id', '')
                          formikProps.setFieldValue('package_group', '')
                          formikProps.setFieldValue('password', '')
                          formikProps.setFieldValue('username', '')
                        }
                      }}
                      options={integrationTypeOptions}
                    />
                  </Col>
                </Row>
                <Row gutter={[12, 0]}>
                  <Col span={6}>
                    <Input
                      data-test-td="code"
                      formikProps={formikProps}
                      label="web.resources.common.code.title"
                      name="code"
                      required={false}
                    />
                  </Col>
                  <Col span={8}>
                    <Input
                      data-test-td="name"
                      formikProps={formikProps}
                      label="web.resources.common.name.title"
                      name="name"
                      required={false}
                    />
                  </Col>
                  <Col span={10}>
                    <SelectServiceHub formikProps={formikProps} />
                  </Col>
                </Row>
                <Row gutter={[12, 0]}>
                  <Col span={10}>
                    <Input
                      data-test-id="username"
                      disabled={isPartner}
                      formikProps={formikProps}
                      label="web.login.username.title"
                      name="username"
                    />
                  </Col>
                  <Col span={10}>
                    <Input
                      data-test-id="password"
                      disabled={isPartner}
                      formikProps={formikProps}
                      label="web.login.password.title"
                      name="password"
                    />
                  </Col>
                  <Col span={4}>
                    <Input
                      data-test-id="branch-id"
                      disabled={isPartner}
                      formikProps={formikProps}
                      label="web.agreement.branch-id"
                      name="branch_id"
                    />
                  </Col>
                </Row>
                <Row gutter={[12, 0]}>
                  <Col span={12}>
                    <Input
                      data-test-id="domain"
                      disabled={isPartner || isServiceAir}
                      formikProps={formikProps}
                      label="web.agreement.domain"
                      name="domain"
                    />
                  </Col>
                  <Col span={12}>
                    <Input
                      data-test-id="access-code"
                      disabled={isPartner || isServiceAir}
                      formikProps={formikProps}
                      label="web.agreement.access-code"
                      name="access_code"
                    />
                  </Col>
                </Row>
                <Row gutter={[12, 0]}>
                  <Col span={8}>
                    <Input
                      data-test-id="package-group"
                      disabled={isPartner || !isServiceAir}
                      formikProps={formikProps}
                      label="web.agreement.package-group"
                      name="package_group"
                    />
                  </Col>
                  <Col span={8}>
                    <Input
                      data-test-id="gtw-agency-id"
                      disabled={isPartner || !isServiceAir}
                      formikProps={formikProps}
                      label="web.agreement.gtw-agency-id"
                      name="gtw_agency_id"
                    />
                  </Col>
                  <Col span={8}>
                    <Input
                      data-test-id="gtw-group-id"
                      disabled={isPartner || !isServiceAir}
                      formikProps={formikProps}
                      label="web.agreement.gtw-group-id"
                      name="gtw_group_id"
                    />
                  </Col>
                </Row>
                <Row gutter={[12, 0]}>
                  <Col span={12}>
                    <InputCurrency
                      dataTestId="fee"
                      formikProps={formikProps}
                      label="web.services.airs.fee"
                      name="fee_value"
                      precision={2}
                    />
                  </Col>
                </Row>
              </Form>
            </Drawer>
          )
        }}
        validationSchema={validationSchema}
      />
    </>
  )
}
